import {
  CriticalIllnessInsuranceType,
  DeathInsuranceType,
  DisabilityInsuranceType,
} from "Components/Features/CheckPrice/Products/Advice/adviceTypes";
import {
  TravelCoverageGroup,
  TravelInsuranceType,
} from "Components/Features/CheckPrice/Products/RiskProducts/Travel";
import {
  InsuranceType,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";
import {
  AccidentCoverage,
  AkaFamilyAccidentCoverage,
  AkaHouseContentsCoverage,
  AnimalCoverage,
  AnimalCoverageGroup,
  BoatCoverage,
  CabinContentsCoverage,
  CabinCoverage,
  CarCoverage,
  CaravanCoverage,
  ChildCoverage,
  CoverageTexts,
  CriticalIllnessCoverage,
  DeathCoverage,
  DisabilityCoverage,
  ElectricScooterCoverage,
  HealthCoverage,
  HouseContentsCoverage,
  HouseCoverage,
  InsuranceCoverage,
  MopedCoverage,
  MotorcycleCoverage,
  OPTIONAL_COVERAGE_VALUE,
  PregnantAndNewbornCoverage,
  TractorCoverage,
  TrailerCoverage,
  TravelCoverage,
  ValuablesCoverage,
  VeteranAutoCoverage,
} from "Store/interfaces/products/coverage";
import { CoverageCardText, TextLanguage } from "Store/texts/interfaces";

import { Option } from "@storeblocks/select";
import { ChildInsuranceType } from "Components/Features/CheckPrice/Products/LifeProducts/Child";
import { HealthInsuranceType } from "Components/Features/CheckPrice/Products/LifeProducts/Health/healthTypes";
import { PregnantAndNewbornInsuranceType } from "Components/Features/CheckPrice/Products/LifeProducts/PregnantAndNewborn/pregnantAndNewbornTypes";
import { AccidentInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/Accident/accidentTypes";
import { AkaHouseContentsInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/AkaHouseContents";
import { CabinInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin/cabinTypes";
import { CabinContentsInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/CabinContents/cabinContentsTypes";
import { HouseInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/houseTypes";
import { HouseContentsInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/HouseContents/houseContentsTypes";
import { ValuablesInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/Valuables";
import { BoatInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Boat/boatTypes";
import { CarInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car";
import { CaravanInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Caravan/constants";
import { ElectricScooterInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/ElectricScooter/interface";
import { MopedInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Moped/mopedInterface";
import { MotorcycleInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Motorcycle/motorcycleInterface";
import { TractorInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Tractor/tractorInterface";
import { TrailerInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/Trailer/trailerInterface";
import { VeteranAutoInsuranceType } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/VeteranAuto/veteranAutoInterface";
import { TextManager } from "Components/TextContext";
import { CalculationPrice } from "Store/priceCalculation/priceCalculationInterfaces";
import { isDefined } from "Utils/functions";
import { ConstEnum } from "Utils/types";
import { CoveragePriceCalculation } from "./coverages.types";

export const INSURANCE_TYPE_VARIANT = {
  EXTENDED_RENTAL: "EXTENDED_RENTAL",
} as const;

export type InsuranceTypeVariantVariable = ConstEnum<
  typeof INSURANCE_TYPE_VARIANT
>;
export function getCoverageLabel(
  coverage: InsuranceCoverage,
  formText: CoverageCardText,
  lang: TextLanguage,
): string {
  const FMSCoverage = mapCoverageToFMSCoverageGroup(coverage);
  return formText.coverages?.[FMSCoverage]?.label[lang] || "";
}

export function getCoverageDescription(
  coverage: InsuranceCoverage,
  formText: CoverageCardText,
  lang: TextLanguage,
): string {
  const FMSCoverage = mapCoverageToFMSCoverageGroup(coverage);
  return formText.coverages?.[FMSCoverage]?.hint[lang] || "";
}

export function getCoverageInfoHeader(
  coverage: InsuranceCoverage,
  formText: CoverageCardText,
  lang: TextLanguage,
): string {
  switch (coverage) {
    case ChildCoverage.EXTRA:
    case HouseCoverage.HOUSE_EXTENDED:
    case HouseContentsCoverage.HOUSE_CONTENTS_EXTENDED:
      return formText.infoHeader[lang];
    default:
      return "";
  }
}

export const mapCoverageToFMSCoverageGroup = (
  coverage: InsuranceCoverage,
): string => {
  switch (coverage) {
    case TravelCoverage.TRAVEL_STANDARD:
    case TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY:
      return TravelCoverageGroup.STANDARD;
    case TravelCoverage.TRAVEL_SUPER:
    case TravelCoverage.TRAVEL_SUPER_90_DAYS:
    case TravelCoverage.TRAVEL_SUPER_120_DAYS:
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY:
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_90_DAYS:
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_120_DAYS:
      return TravelCoverageGroup.SUPER;
    case AnimalCoverage.CAT_DEATH:
    case AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE:
      return AnimalCoverageGroup.ANIMAL_DEATH;
    case AnimalCoverage.CAT_VET:
    case AnimalCoverage.DOG_VET:
      return AnimalCoverageGroup.ANIMAL_VETERINARY;
    default:
      return coverage;
  }
};

export function getAdditionalCoverageInfo(
  coverage: InsuranceCoverage,
  formText: CoverageCardText,
  lang: TextLanguage,
): string {
  const FMSCoverage = mapCoverageToFMSCoverageGroup(coverage);
  return FMSCoverage === HouseCoverage.HOUSE_EXTENDED
    ? formText.additionalInfo[lang]
    : "";
}

export const getBetterInsuranceLabel = (
  coverage: InsuranceCoverage,
): string => {
  const coverageGroup = mapCoverageToFMSCoverageGroup(coverage);
  const { getText } = TextManager;

  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (coverageGroup) {
    case AccidentCoverage.ACCIDENT_DEATH:
      return getText("coveragecard.accident.betterinsurancelabel-disablement");
    case AccidentCoverage.ACCIDENT_DISABLEMENT:
      return getText("coveragecard.accident.betterinsurancelabel-death");
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH:
      return getText(
        "coveragecard.akafamilyaccident.betterinsurancelabel-disablement",
      );
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DISABILITY:
      return getText(
        "coveragecard.akafamilyaccident.betterinsurancelabel-death-and-professional-disability",
      );
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DISABILITY_AND_PROFESSIONAL_DISABILITY:
      return getText(
        "coveragecard.akafamilyaccident.betterinsurancelabel-death",
      );
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH_AND_DISABILITY:
      return getText(
        "coveragecard.akafamilyaccident.betterinsurancelabel-disablement-professional",
      );
    case BoatCoverage.BOAT_MOTOR_FULL_OWN_DAMAGE:
    case BoatCoverage.BOAT_SAILING_BOAT_FULL_OWN_DAMAGE:
      return getText("coveragecard.boat.coveragelabel-boat_super");
    case BoatCoverage.BOAT_MOTOR_REDUCED_OWN_DAMAGE:
    case BoatCoverage.BOAT_ROWBOAT_CANOE_KAYAK_REDUCED_OWN_DAMAGE:
    case BoatCoverage.BOAT_SAILING_BOAT_REDUCED_OWN_DAMAGE:
      return getText("coveragecard.boat.coveragelabel-boat_full_own_damage");
    case CabinCoverage.CABIN_STANDARD:
      return getText("coveragecard.cabin.coveragelabel-cabin_extended");
    case CabinContentsCoverage.CABIN_CONTENTS_STANDARD:
      return getText(
        "coveragecard.cabincontents.coveragelabel-cabin_contents_extended",
      );
    case HouseCoverage.HOUSE_STANDARD:
      return getText("coveragecard.house.coveragelabel-house_extended");
    case HouseContentsCoverage.HOUSE_CONTENTS_STANDARD:
      return getText(
        "coveragecard.housecontents.coveragelabel-house_contents_standard_extended",
      );
    case ChildCoverage.BASIC:
      return getText("coveragecard.child.coveragelabel-newchild_extra");
    case ChildCoverage.EXTRA:
      return getText("coveragecard.child.coveragelabel-newchild_total");
    case TravelCoverageGroup.STANDARD:
      return getText("coveragecard.travel.coveragelabel-travel_super");
    case TractorCoverage.TRACTOR_LIABILITY:
      return getText(
        "coveragecard.tractor.coveragelabel-tractor_fire_and_theft",
      );
    case TractorCoverage.TRACTOR_FIRE_AND_THEFT:
      return getText(
        "coveragecard.tractor.coveragelabel-tractor_full_own_damage",
      );
    case TrailerCoverage.TRAILER_FIRE_AND_THEFT:
      return getText(
        "coveragecard.trailer.coveragelabel-trailer_full_own_damage",
      );
    case CaravanCoverage.CARAVAN_FIRE_AND_THEFT:
      return getText(
        "coveragecard.caravan.coveragelabel-caravan_full_own_damage",
      );
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_MOVABLES_1:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_MOVABLES_2:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_MOVABLES_3:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE_MOVABLES_1:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE_MOVABLES_2:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE_MOVABLES_3:
      return getText("coveragecard.caravan.coveragelabel-caravan_super");
    case CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT:
      return getText("coveragecard.car.coveragelabel-auto_reduced_own_damage");
    case CarCoverage.AUTO_REDUCED_OWN_DAMAGE:
      return getText("coveragecard.car.coveragelabel-auto_full_own_damage");
    case CarCoverage.AUTO_FULL_OWN_DAMAGE:
    case CarCoverage.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR:
      return getText("coveragecard.car.coveragelabel-auto_full_super");
    case CarCoverage.AUTO_FULL_SUPER_V2:
      return getText(
        "coveragecard.car.coveragelabel-auto_full_super_v2_including_rentalcar",
      );
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY:
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY_WITH_ACCIDENT:
      return getText(
        "coveragecard.electricscooter.coveragelabel-electric_scooter_reduced_own_damage",
      );
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE:
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE_WITH_ACCIDENT:
      return getText(
        "coveragecard.electricscooter.coveragelabel-electric_scooter_full_own_damage",
      );
    case MopedCoverage.MOPED_DRIVER_AND_PASSENGER_ACCIDENT:
      return getText(
        "coveragecard.moped.coveragelabel-moped_reduced_own_damage",
      );
    case MopedCoverage.MOPED_REDUCED_OWN_DAMAGE:
      return getText("coveragecard.moped.coveragelabel-moped_full_own_damage");
    case MotorcycleCoverage.MOTORCYCLE_DRIVER_AND_PASSENGER_ACCIDENT:
      return getText(
        "coveragecard.motorcycle.coveragelabel-motorcycle_reduced_own_damage",
      );
    case MotorcycleCoverage.MOTORCYCLE_REDUCED_OWN_DAMAGE:
      return getText(
        "coveragecard.motorcycle.coveragelabel-motorcycle_full_own_damage",
      );
    case VeteranAutoCoverage.VETERAN_AUTO_REDUCED_OWN_DAMAGE:
      return getText(
        "coveragecard.veteranauto.coveragelabel-veteran_auto_full_own_damage",
      );
    case AnimalCoverageGroup.ANIMAL_DEATH:
      return getText("coveragecard.animal.betterinsurancelabel_death");
    case AnimalCoverageGroup.ANIMAL_VETERINARY:
      return getText("coveragecard.animal.betterinsurancelabel_vet");
    case HealthCoverage.HEALTH_BASIS:
    case HealthCoverage.HEALTH_PLUS:
      return getText("coveragecard.health.betterinsurancelabel-plus");
    default:
      return "";
  }
};

export const getCoveragesFromInsuranceType = (
  coverage: InsuranceType,
): InsuranceCoverage[] => {
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (coverage) {
    case AkaHouseContentsInsuranceType.AKA_HOUSE_CONTENTS_STANDARD:
      return [AkaHouseContentsCoverage.AKA_HOUSE_CONTENTS_STANDARD];
    case CabinInsuranceType.CABIN_STANDARD:
      return [CabinCoverage.CABIN_STANDARD];
    case CabinInsuranceType.CABIN_SUPER:
      return [CabinCoverage.CABIN_EXTENDED];
    case CabinContentsInsuranceType.CABIN_CONTENTS_STANDARD:
      return [CabinContentsCoverage.CABIN_CONTENTS_STANDARD];
    case CabinContentsInsuranceType.CABIN_CONTENTS_SUPER:
      return [CabinContentsCoverage.CABIN_CONTENTS_EXTENDED];
    case CarInsuranceType.AUTO_DRIVER_AND_PASSENGER_ACCIDENT:
      return [CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT];
    case CarInsuranceType.AUTO_REDUCED_OWN_DAMAGE:
      return [CarCoverage.AUTO_REDUCED_OWN_DAMAGE];
    case CarInsuranceType.AUTO_FULL_OWN_DAMAGE:
    case CarInsuranceType.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR:
      return [
        CarCoverage.AUTO_FULL_OWN_DAMAGE,
        CarCoverage.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR,
      ];
    case CarInsuranceType.AUTO_FULL_SUPER_V2:
    case CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR:
      return [
        CarCoverage.AUTO_FULL_SUPER_V2,
        CarCoverage.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR,
      ];
    case HealthInsuranceType.HEALTH_BASIS:
      return [HealthCoverage.HEALTH_BASIS];
    case HealthInsuranceType.HEALTH_PLUS:
      return [HealthCoverage.HEALTH_PLUS];
    case HealthInsuranceType.HEALTH_TOP:
      return [HealthCoverage.HEALTH_TOP];
    case HouseInsuranceType.HOUSE_STANDARD:
      return [HouseCoverage.HOUSE_STANDARD];
    case HouseInsuranceType.HOUSE_SUPER:
      return [HouseCoverage.HOUSE_EXTENDED];
    case HouseContentsInsuranceType.HOUSE_CONTENTS_STANDARD:
      return [HouseContentsCoverage.HOUSE_CONTENTS_STANDARD];
    case HouseContentsInsuranceType.HOUSE_CONTENTS_SUPER:
      return [HouseContentsCoverage.HOUSE_CONTENTS_EXTENDED];
    case ChildInsuranceType.NEWCHILD_BASIC:
      return [ChildCoverage.BASIC];
    case ChildInsuranceType.NEWCHILD_EXTRA:
      return [ChildCoverage.EXTRA];
    case ChildInsuranceType.NEWCHILD_TOTAL:
      return [ChildCoverage.TOTAL];
    case PregnantAndNewbornInsuranceType.NEWBORN:
      return [
        PregnantAndNewbornCoverage.NEWBORN,
        PregnantAndNewbornCoverage.NEWBORN_MULTIPLE_CHILDREN,
      ];
    case PregnantAndNewbornInsuranceType.PREGNANT:
      return [
        PregnantAndNewbornCoverage.PREGNANT,
        PregnantAndNewbornCoverage.PREGNANT_MULTIPLE_CHILDREN,
      ];
    case TravelInsuranceType.TRAVEL_STANDARD:
      return [
        TravelCoverage.TRAVEL_STANDARD,
        TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY,
      ];
    case TravelInsuranceType.TRAVEL_SUPER:
      return [
        TravelCoverage.TRAVEL_SUPER,
        TravelCoverage.TRAVEL_SUPER_120_DAYS,
        TravelCoverage.TRAVEL_SUPER_90_DAYS,
        TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY,
        TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_120_DAYS,
        TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_90_DAYS,
      ];
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_LIABILITY:
      return [
        ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY,
        ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY_WITH_ACCIDENT,
      ];
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE:
      return [
        ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE,
        ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE_WITH_ACCIDENT,
      ];
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE:
      return [
        ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE,
        ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE_WITH_ACCIDENT,
      ];
    case MopedInsuranceType.MOPED_DRIVER_AND_PASSENGER_ACCIDENT:
      return [MopedCoverage.MOPED_DRIVER_AND_PASSENGER_ACCIDENT];
    case MopedInsuranceType.MOPED_REDUCED_OWN_DAMAGE:
      return [MopedCoverage.MOPED_REDUCED_OWN_DAMAGE];
    case MopedInsuranceType.MOPED_FULL_OWN_DAMAGE:
      return [MopedCoverage.MOPED_FULL_OWN_DAMAGE];
    case MotorcycleInsuranceType.MOTORCYCLE_DRIVER_AND_PASSENGER_ACCIDENT:
      return [MotorcycleCoverage.MOTORCYCLE_DRIVER_AND_PASSENGER_ACCIDENT];
    case MotorcycleInsuranceType.MOTORCYCLE_REDUCED_OWN_DAMAGE:
      return [MotorcycleCoverage.MOTORCYCLE_REDUCED_OWN_DAMAGE];
    case MotorcycleInsuranceType.MOTORCYCLE_FULL_OWN_DAMAGE:
      return [MotorcycleCoverage.MOTORCYCLE_FULL_OWN_DAMAGE];
    case TractorInsuranceType.TRACTOR_LIABILITY:
      return [TractorCoverage.TRACTOR_LIABILITY];
    case TractorInsuranceType.TRACTOR_FIRE_AND_THEFT:
      return [TractorCoverage.TRACTOR_FIRE_AND_THEFT];
    case TractorInsuranceType.TRACTOR_FULL_OWN_DAMAGE:
      return [TractorCoverage.TRACTOR_FULL_OWN_DAMAGE];
    case VeteranAutoInsuranceType.VETERAN_AUTO_REDUCED_OWN_DAMAGE:
      return [VeteranAutoCoverage.VETERAN_AUTO_REDUCED_OWN_DAMAGE];
    case VeteranAutoInsuranceType.VETERAN_AUTO_FULL_OWN_DAMAGE:
      return [VeteranAutoCoverage.VETERAN_AUTO_FULL_OWN_DAMAGE];
    case BoatInsuranceType.BOAT_SUPER:
      return [
        BoatCoverage.BOAT_MOTOR_FULL_OWN_DAMAGE_EXTENDED,
        BoatCoverage.BOAT_SAILING_BOAT_FULL_OWN_DAMAGE_EXTENDED,
      ];
    case BoatInsuranceType.BOAT_FULL_OWN_DAMAGE:
      return [
        BoatCoverage.BOAT_MOTOR_FULL_OWN_DAMAGE,
        BoatCoverage.BOAT_SAILING_BOAT_FULL_OWN_DAMAGE,
        BoatCoverage.BOAT_ROWBOAT_CANOE_KAYAK_FULL_OWN_DAMAGE,
      ];
    case BoatInsuranceType.BOAT_REDUCED_OWN_DAMAGE:
      return [
        BoatCoverage.BOAT_MOTOR_REDUCED_OWN_DAMAGE,
        BoatCoverage.BOAT_SAILING_BOAT_REDUCED_OWN_DAMAGE,
        BoatCoverage.BOAT_ROWBOAT_CANOE_KAYAK_REDUCED_OWN_DAMAGE,
      ];
    case AccidentInsuranceType.ACCIDENT_DEATH:
      return [AccidentCoverage.ACCIDENT_DEATH];
    case AccidentInsuranceType.ACCIDENT_DISABLEMENT:
      return [AccidentCoverage.ACCIDENT_DISABLEMENT];
    default:
      return [];
  }
};

export const coverageAsOptions = (
  coverageGroupCoverages: InsuranceCoverage[],
  formTexts: any,
  lang: string,
): Option[] => {
  return coverageGroupCoverages.map((coverage) => ({
    label: formTexts[coverage][lang],
    value: coverage,
  }));
};

export const getSelectedCoverage = (
  selectedCoverage: InsuranceCoverage,
  coveragePrices: CoveragePriceCalculation[],
): CoveragePriceCalculation | undefined =>
  coveragePrices.find(([coverage]) => coverage === selectedCoverage);

export const getInsuranceTypeCoverages = (
  coverages: CoverageTexts[],
  insuranceType: InsuranceType | null,
): CoverageTexts[] => {
  return coverages.filter(
    (coverage) => insuranceType && coverage[insuranceType] === true,
  );
};

export const getInsuranceTypeExcludedCoverages = (
  coverages: CoverageTexts[],
  insuranceType: InsuranceType | null,
  betterInsuranceType: InsuranceType | null,
): CoverageTexts[] =>
  coverages.filter((coverage) => {
    if (betterInsuranceType) {
      return (
        insuranceType &&
        coverage[insuranceType] !== true &&
        coverage[betterInsuranceType] === true
      );
    }
    return insuranceType && coverage[insuranceType] !== true;
  });

export const getOptionalInsuranceCoverages = (
  coverages: CoverageTexts[],
  insuranceType: InsuranceType | null,
): CoverageTexts[] => {
  return coverages.filter((coverage) => {
    if (insuranceType === null) {
      return false;
    }
    return coverage[insuranceType] === OPTIONAL_COVERAGE_VALUE;
  });
};

export const insuranceTypeFromInsuranceCoverage = (
  coverage: InsuranceCoverage,
  variant?: InsuranceTypeVariantVariable,
): InsuranceType | null => {
  const FMSCoverage = mapCoverageToFMSCoverageGroup(coverage);
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (FMSCoverage) {
    case AkaHouseContentsCoverage.AKA_HOUSE_CONTENTS_STANDARD:
      return AkaHouseContentsInsuranceType.AKA_HOUSE_CONTENTS_STANDARD;
    case AccidentCoverage.ACCIDENT_DEATH:
      return AccidentInsuranceType.ACCIDENT_DEATH;
    case AccidentCoverage.ACCIDENT_DISABLEMENT:
      return AccidentInsuranceType.ACCIDENT_DISABLEMENT;
    case BoatCoverage.BOAT_MOTOR_FULL_OWN_DAMAGE_EXTENDED:
    case BoatCoverage.BOAT_SAILING_BOAT_FULL_OWN_DAMAGE_EXTENDED:
      return BoatInsuranceType.BOAT_SUPER;
    case BoatCoverage.BOAT_MOTOR_FULL_OWN_DAMAGE:
    case BoatCoverage.BOAT_ROWBOAT_CANOE_KAYAK_FULL_OWN_DAMAGE:
    case BoatCoverage.BOAT_SAILING_BOAT_FULL_OWN_DAMAGE:
      return BoatInsuranceType.BOAT_FULL_OWN_DAMAGE;
    case BoatCoverage.BOAT_MOTOR_REDUCED_OWN_DAMAGE:
    case BoatCoverage.BOAT_ROWBOAT_CANOE_KAYAK_REDUCED_OWN_DAMAGE:
    case BoatCoverage.BOAT_SAILING_BOAT_REDUCED_OWN_DAMAGE:
      return BoatInsuranceType.BOAT_REDUCED_OWN_DAMAGE;
    case CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT:
      return CarInsuranceType.AUTO_DRIVER_AND_PASSENGER_ACCIDENT;
    case CarCoverage.AUTO_REDUCED_OWN_DAMAGE:
      return CarInsuranceType.AUTO_REDUCED_OWN_DAMAGE;
    case CarCoverage.AUTO_FULL_OWN_DAMAGE:
      return CarInsuranceType.AUTO_FULL_OWN_DAMAGE;
    case CarCoverage.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR:
      if (variant === INSURANCE_TYPE_VARIANT.EXTENDED_RENTAL) {
        return CarInsuranceType.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR_EXTENDED;
      }
      return CarInsuranceType.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR;
    case CarCoverage.AUTO_FULL_SUPER:
      return CarInsuranceType.AUTO_FULL_SUPER_V2;
    case CarCoverage.AUTO_FULL_SUPER_V2:
      return CarInsuranceType.AUTO_FULL_SUPER_V2;
    case CarCoverage.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR:
      if (variant === INSURANCE_TYPE_VARIANT.EXTENDED_RENTAL) {
        return CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR_EXTENDED;
      }
      return CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR;
    case CaravanCoverage.CARAVAN_FIRE_AND_THEFT:
      return CaravanInsuranceType.CARAVAN_FIRE_AND_THEFT;
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_MOVABLES_1:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_MOVABLES_2:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_MOVABLES_3:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE_MOVABLES_1:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE_MOVABLES_2:
    case CaravanCoverage.CARAVAN_FULL_OWN_DAMAGE_EXTENDED_PERMANENT_PLACE_MOVABLES_3:
      return CaravanInsuranceType.CARAVAN_FULL_OWN_DAMAGE;
    case CaravanCoverage.CARAVAN_SUPER:
      return CaravanInsuranceType.CARAVAN_SUPER;
    case ChildCoverage.BASIC:
      return ChildInsuranceType.NEWCHILD_BASIC;
    case ChildCoverage.EXTRA:
      return ChildInsuranceType.NEWCHILD_EXTRA;
    case ChildCoverage.TOTAL:
      return ChildInsuranceType.NEWCHILD_TOTAL;
    case CriticalIllnessCoverage.CRITICAL_ILLNESS_NOT_SMOKER:
      return CriticalIllnessInsuranceType.CRITICAL_ILLNESS_NOT_SMOKER;
    case DeathCoverage.DEATH_NOT_SMOKER:
      return DeathInsuranceType.DEATH_NOT_SMOKER;
    case DisabilityCoverage.DISABILITY_FLEX:
      return DisabilityInsuranceType.DISABILITY_FLEX;
    case PregnantAndNewbornCoverage.NEWBORN:
    case PregnantAndNewbornCoverage.NEWBORN_MULTIPLE_CHILDREN:
      return PregnantAndNewbornInsuranceType.NEWBORN;
    case PregnantAndNewbornCoverage.PREGNANT:
    case PregnantAndNewbornCoverage.PREGNANT_MULTIPLE_CHILDREN:
      return PregnantAndNewbornInsuranceType.PREGNANT;
    case HealthCoverage.HEALTH_BASIS:
      return HealthInsuranceType.HEALTH_BASIS;
    case HealthCoverage.HEALTH_PLUS:
      return HealthInsuranceType.HEALTH_PLUS;
    case HealthCoverage.HEALTH_TOP:
      return HealthInsuranceType.HEALTH_TOP;
    case HouseCoverage.HOUSE_STANDARD:
      return HouseInsuranceType.HOUSE_STANDARD;
    case HouseCoverage.HOUSE_EXTENDED:
      return HouseInsuranceType.HOUSE_SUPER;
    case HouseContentsCoverage.HOUSE_CONTENTS_STANDARD:
      return HouseContentsInsuranceType.HOUSE_CONTENTS_STANDARD;
    case HouseContentsCoverage.HOUSE_CONTENTS_EXTENDED:
      return HouseContentsInsuranceType.HOUSE_CONTENTS_SUPER;
    case CabinCoverage.CABIN_STANDARD:
      return CabinInsuranceType.CABIN_STANDARD;
    case CabinCoverage.CABIN_EXTENDED:
      return CabinInsuranceType.CABIN_SUPER;
    case CabinContentsCoverage.CABIN_CONTENTS_STANDARD:
      return CabinContentsInsuranceType.CABIN_CONTENTS_STANDARD;
    case CabinContentsCoverage.CABIN_CONTENTS_EXTENDED:
      return CabinContentsInsuranceType.CABIN_CONTENTS_SUPER;
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_LIABILITY;
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY_WITH_ACCIDENT:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_LIABILITY_WITH_ACCIDENT;
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE;
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE_WITH_ACCIDENT:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE_WITH_ACCIDENT;
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE;
    case ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE_WITH_ACCIDENT:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE_WITH_ACCIDENT;
    case MopedCoverage.MOPED_DRIVER_AND_PASSENGER_ACCIDENT:
      return MopedInsuranceType.MOPED_DRIVER_AND_PASSENGER_ACCIDENT;
    case MopedCoverage.MOPED_REDUCED_OWN_DAMAGE:
      return MopedInsuranceType.MOPED_REDUCED_OWN_DAMAGE;
    case MopedCoverage.MOPED_FULL_OWN_DAMAGE:
      return MopedInsuranceType.MOPED_FULL_OWN_DAMAGE;
    case MotorcycleCoverage.MOTORCYCLE_DRIVER_AND_PASSENGER_ACCIDENT:
      return MotorcycleInsuranceType.MOTORCYCLE_DRIVER_AND_PASSENGER_ACCIDENT;
    case MotorcycleCoverage.MOTORCYCLE_REDUCED_OWN_DAMAGE:
      return MotorcycleInsuranceType.MOTORCYCLE_REDUCED_OWN_DAMAGE;
    case MotorcycleCoverage.MOTORCYCLE_FULL_OWN_DAMAGE:
      return MotorcycleInsuranceType.MOTORCYCLE_FULL_OWN_DAMAGE;
    case TractorCoverage.TRACTOR_LIABILITY:
      return TractorInsuranceType.TRACTOR_LIABILITY;
    case TractorCoverage.TRACTOR_FIRE_AND_THEFT:
      return TractorInsuranceType.TRACTOR_FIRE_AND_THEFT;
    case TractorCoverage.TRACTOR_FULL_OWN_DAMAGE:
      return TractorInsuranceType.TRACTOR_FULL_OWN_DAMAGE;
    case TrailerCoverage.TRAILER_FULL_OWN_DAMAGE:
      return TrailerInsuranceType.TRAILER_FULL_OWN_DAMAGE;
    case TrailerCoverage.TRAILER_FIRE_AND_THEFT:
      return TrailerInsuranceType.TRAILER_FIRE_AND_THEFT;
    case TravelCoverageGroup.STANDARD:
      return TravelInsuranceType.TRAVEL_STANDARD;
    case TravelCoverageGroup.SUPER:
      return TravelInsuranceType.TRAVEL_SUPER;
    case ValuablesCoverage.VALUABLES:
      return ValuablesInsuranceType.VALUABLES;
    case VeteranAutoCoverage.VETERAN_AUTO_REDUCED_OWN_DAMAGE:
      return VeteranAutoInsuranceType.VETERAN_AUTO_REDUCED_OWN_DAMAGE;
    case VeteranAutoCoverage.VETERAN_AUTO_FULL_OWN_DAMAGE:
      return VeteranAutoInsuranceType.VETERAN_AUTO_FULL_OWN_DAMAGE;
    default:
      return null;
  }
};

export const betterInsuranceTypeFromInsuranceType = (
  insuranceType: InsuranceType | null,
): InsuranceType | null => {
  switch (insuranceType) {
    case BoatInsuranceType.BOAT_REDUCED_OWN_DAMAGE:
      return BoatInsuranceType.BOAT_FULL_OWN_DAMAGE;
    case BoatInsuranceType.BOAT_FULL_OWN_DAMAGE:
      return BoatInsuranceType.BOAT_SUPER;
    case ChildInsuranceType.NEWCHILD_BASIC:
      return ChildInsuranceType.NEWCHILD_EXTRA;
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_LIABILITY:
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_LIABILITY_WITH_ACCIDENT:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE;
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE:
    case ElectricScooterInsuranceType.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE_WITH_ACCIDENT:
      return ElectricScooterInsuranceType.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE;
    case MopedInsuranceType.MOPED_DRIVER_AND_PASSENGER_ACCIDENT:
      return MopedInsuranceType.MOPED_REDUCED_OWN_DAMAGE;
    case MopedInsuranceType.MOPED_REDUCED_OWN_DAMAGE:
      return MopedInsuranceType.MOPED_FULL_OWN_DAMAGE;
    case MotorcycleInsuranceType.MOTORCYCLE_DRIVER_AND_PASSENGER_ACCIDENT:
      return MotorcycleInsuranceType.MOTORCYCLE_REDUCED_OWN_DAMAGE;
    case MotorcycleInsuranceType.MOTORCYCLE_REDUCED_OWN_DAMAGE:
      return MotorcycleInsuranceType.MOTORCYCLE_FULL_OWN_DAMAGE;
    case TractorInsuranceType.TRACTOR_LIABILITY:
      return TractorInsuranceType.TRACTOR_FIRE_AND_THEFT;
    case TractorInsuranceType.TRACTOR_FIRE_AND_THEFT:
      return TractorInsuranceType.TRACTOR_FULL_OWN_DAMAGE;
    case CarInsuranceType.AUTO_DRIVER_AND_PASSENGER_ACCIDENT:
      return CarInsuranceType.AUTO_REDUCED_OWN_DAMAGE;
    case CarInsuranceType.AUTO_REDUCED_OWN_DAMAGE:
      return CarInsuranceType.AUTO_FULL_OWN_DAMAGE;
    case CarInsuranceType.AUTO_FULL_OWN_DAMAGE:
      return CarInsuranceType.AUTO_FULL_SUPER_V2;
    case CarInsuranceType.AUTO_FULL_SUPER_V2:
      return CarInsuranceType.AUTO_FULL_SUPER_V2;
    case CarInsuranceType.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR:
      return CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR;
    case CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR:
      return CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR;
    case CarInsuranceType.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR_EXTENDED:
      return CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR_EXTENDED;
    case CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR_EXTENDED:
      return CarInsuranceType.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR_EXTENDED;
    case VeteranAutoInsuranceType.VETERAN_AUTO_REDUCED_OWN_DAMAGE:
      return VeteranAutoInsuranceType.VETERAN_AUTO_FULL_OWN_DAMAGE;
    default:
      return null;
  }
};

export const coveragePriceDifference = (
  original: CoveragePriceCalculation,
  compared: CoveragePriceCalculation,
): number =>
  original[1].prices[0].monthlyPrice - compared[1].prices[0].monthlyPrice;

export const getCoveragePrice = (
  coverages: CoveragePriceCalculation[],
  coverage: InsuranceCoverage,
  filterValue?: string,
): CalculationPrice => {
  const coveragePriceCalculation = coverages.filter(
    ([mappedCoverage]) => mappedCoverage === coverage,
  )[0];

  const prices = coveragePriceCalculation[1].prices;

  const selectedPrices = filterValue
    ? prices.filter(({ value }) => !filterValue || value === filterValue)
    : prices;

  return selectedPrices[0];
};

export const dropIrrelevantCoverage = (
  coverages: CoverageTexts[],
  coverage: InsuranceType | null,
  coverageToDrop?: InsuranceType,
): CoverageTexts[] =>
  coverages
    .map((c) => toCoverageWithoutType(c, coverageToDrop))
    .filter((c) => removeIrrelevantCoverageType(c, coverage, coverageToDrop));

export const filterOutIrrelevantCoverage = (
  coverages: CoverageTexts[],
  coverage: InsuranceType | null,
  coverageToDrop?: InsuranceType,
): CoverageTexts[] =>
  coverages.filter((c) => {
    const betterInsuranceType = betterInsuranceTypeFromInsuranceType(coverage);
    if (
      isDefined(coverage) &&
      isDefined(coverageToDrop) &&
      betterInsuranceType === coverageToDrop
    ) {
      return c[coverage] === true && c[coverageToDrop] === true;
    }
    return true;
  });

const toCoverageWithoutType = (
  coverage: CoverageTexts,
  type?: InsuranceType,
): CoverageTexts => {
  const c = { ...coverage };
  if (type) {
    try {
      delete c[type];
    } catch (e) {
      // Do nothing.
    }
  }
  return c;
};

const removeIrrelevantCoverageType = (
  coverage: CoverageTexts,
  insuranceType: InsuranceType | null,
  coverageToDrop?: InsuranceType,
): boolean => {
  const betterInsuranceType =
    betterInsuranceTypeFromInsuranceType(insuranceType);
  if (isDefined(coverageToDrop) && betterInsuranceType === coverageToDrop) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { caption, explanation, optional, ...rest } = coverage;
    return Object.values(rest).some((value) => value !== false);
  }
  return true;
};

const getCoverageCountMap = (
  coverages: CoverageTexts[],
  types: InsuranceType[],
): Record<InsuranceType, number> => {
  const countMap: Record<string, number> = {};

  coverages.forEach((coverage) => {
    types.forEach((type) => {
      if (!isDefined(countMap[type])) {
        countMap[type] = 0;
      }

      if (coverage[type] === true) {
        countMap[type] += 1;
      }
    });
  });

  return countMap as Record<InsuranceType, number>;
};

export const getRelevantHeaders = (
  coverageAccordion2: any,
  product: ProductBackendName,
  coverages: CoverageTexts[],
): [InsuranceType, Record<TextLanguage, string>][] => {
  const headers = Object.entries(coverageAccordion2[product]) as [
    InsuranceType,
    Record<TextLanguage, string>,
  ][];

  const countMap = getCoverageCountMap(
    coverages,
    headers.map(([type]) => type),
  );

  // Only return the headers that have at least one coverage.
  return headers
    .sort((a, b) => countMap[a[0]] - countMap[b[0]])
    .filter((header) => countMap[header[0]] > 0);
};

/**
 * Using unicode/U+2022 to style strings from FMS as bullet points.
 * Doesn't create trouble for screen readers.
 */
export const needsBulletPointStyling = (s: string): boolean =>
  s.startsWith("•");

export const getDescriptionText = (
  customDescription: string | undefined,
  standardDescription: string | undefined,
): string | undefined => {
  if (isDefined(customDescription)) {
    return customDescription;
  } else if (isDefined(standardDescription)) {
    return standardDescription;
  }
  return undefined;
};
